import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, GraduationCap, Calendar, ArrowRight, Cpu, Building2, Leaf, Users, MapPin, Quote } from 'lucide-react';

const Afrika = () => {
  return (
    <div className="afrika-page font-body text-brand-dark-blue overflow-x-hidden">
      {/* 1. HERO */}
      <section className="relative bg-brand-dark-blue text-white min-h-[85vh] flex items-center overflow-x-clip">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '400px', backgroundRepeat: 'repeat' }} />
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-brand-orange/20 rounded-full blur-[150px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full min-w-0 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center min-w-0">
            {/* TEXT */}
            <div className="order-2 lg:order-1 max-w-xl min-w-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-brand-orange text-sm font-bold uppercase tracking-wide mb-8 backdrop-blur-sm">
                <Globe size={14} className="animate-pulse" />
                Moja misia
              </div>

              <h1 className="text-[1.75rem] min-[400px]:text-3xl sm:text-4xl lg:text-7xl font-heading font-bold mb-8 text-white leading-[1.12] sm:leading-[1.1] break-words">
                Moja misia <br /><span className="text-brand-orange">v Afrike</span>
              </h1>

              <p className="text-base sm:text-xl lg:text-2xl text-gray-200 mb-10 leading-relaxed font-light">
                Afriku vnímam ako región s <span className="text-white font-bold">obrovským potenciálom</span>.
                <br /><span className="text-gray-400 text-lg mt-4 block">Spájam slovenské know-how s rastúcimi trhmi a pomáham mladým ľuďom získať prístup k moderným technológiám.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#export" className="inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-8 rounded-xl transition-all duration-300 shadow-xl hover:shadow-orange-500/20 text-lg group">
                  Spoznať misiu
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* PHOTO */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 transform -rotate-2 hover:rotate-0 transition-all duration-700">
                <div className="aspect-[4/3]">
                  <img src="/afrika_portret.jpeg" alt="Peťo Sloboda na pracovnom stretnutí v Afrike" className="w-full h-full object-cover object-center" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark-blue/80 to-transparent opacity-40" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MEDZINÁRODNÝ EXPORT A SPOLUPRÁCA */}
      <section id="export" className="py-16 sm:py-24 bg-white overflow-x-clip">
        <div className="container mx-auto px-4 sm:px-5 max-w-6xl min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* TEXT */}
            <div className="order-2 lg:order-1 relative">
              <div className="absolute left-0 sm:-left-8 -top-10 text-6xl sm:text-8xl font-heading font-black text-gray-50 -z-10 pointer-events-none select-none" aria-hidden>01</div>
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-brand-dark-blue mb-6">
                <Globe size={24} />
              </div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-6 text-brand-dark-blue underline decoration-brand-orange/20 decoration-4 underline-offset-8">
                Medzinárodný export a spolupráca
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Afrika disponuje aktívne rastúcimi trhmi a veľkým nerastným bohatstvom, no chýba jej odborné know-how v mnohých oblastiach – moderné strojárstvo, poľnohospodárstvo, technológie či robotika. Práve tu vzniká priestor na spoločnú medzinárodnú spoluprácu, ktorá je jednou z hlavných činností mňa a <strong>SABIC-u (Slovak and African Business Council)</strong>.
                </p>
                <p>
                  Pomáham s exportom a nadväzovaním spolupráce na lokálnych trhoch veľkým projektom, súkromným firmám, ale aj štátnym a vzdelávacím inštitúciám tak, aby vznikali <strong>aktívne a dlhodobé obchodné vzťahy</strong>, ktoré prinesú pridanú hodnotu obom regiónom – či už ide o obchod so surovinami, vzdelávanie alebo energetiku.
                </p>
              </div>

              {/* OBLASTI */}
              <div className="grid grid-cols-2 gap-4 mt-10">
                {[
                  { icon: Building2, label: 'Moderné strojárstvo' },
                  { icon: Leaf, label: 'Poľnohospodárstvo' },
                  { icon: Cpu, label: 'Technológie & robotika' },
                  { icon: Globe, label: 'Suroviny & energetika' },
                ].map((area, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3 border border-gray-100">
                    <area.icon size={20} className="text-brand-orange shrink-0" />
                    <span className="text-sm font-semibold text-brand-dark-blue leading-tight">{area.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* PHOTO */}
            <div className="order-1 lg:order-2">
              <figure className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl border border-gray-100">
                  <img src="/afrika_prednaska_export.jpeg" alt="Peťo Sloboda počas prednášky o slovensko-africkej spolupráci" className="w-full h-[420px] sm:h-[520px] object-cover object-center hover:scale-[1.02] transition-transform duration-700" />
                </div>
                <figcaption className="mt-4 text-sm text-gray-500 italic flex items-center gap-2">
                  <MapPin size={14} className="text-brand-orange shrink-0" />
                  Prezentácia inovatívnych riešení pre lokálny priemysel.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* 3. VZDELÁVANIE */}
      <section className="py-16 sm:py-24 bg-gray-50 overflow-x-clip">
        <div className="container mx-auto px-4 sm:px-5 max-w-6xl min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* PHOTO */}
            <div className="order-1">
              <figure className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl border border-gray-100 transform rotate-1 hover:rotate-0 transition-all duration-700">
                  <img src="/afrika_prednaska_juba.jpeg" alt="Prednáška Possibilities of Slovak–South Sudanese Cooperation" className="w-full h-[480px] sm:h-[600px] object-cover object-center" />
                </div>
                <figcaption className="mt-4 text-sm text-gray-500 italic flex items-center gap-2">
                  <MapPin size={14} className="text-brand-orange shrink-0" />
                  „Possibilities of Slovak–South Sudanese Cooperation“ – inovácie, rozvoj zručností a tvorba pracovných miest.
                </figcaption>
              </figure>
            </div>

            {/* TEXT */}
            <div className="order-2 relative">
              <div className="absolute right-0 sm:-right-8 -top-10 text-6xl sm:text-8xl font-heading font-black text-gray-100 -z-10 pointer-events-none select-none" aria-hidden>02</div>
              <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-brand-orange mb-6">
                <GraduationCap size={24} />
              </div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-6 text-brand-dark-blue underline decoration-brand-orange/20 decoration-4 underline-offset-8">
                Vzdelávanie
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Jednou z kľúčových tém v tomto regióne je vzdelávanie mladých ľudí. Tí však aktuálne nemajú najlepšie podmienky na svoj rozvoj.
                </p>
              </div>

              {/* STAT CALLOUT */}
              <div className="my-8 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-4xl sm:text-5xl font-heading font-black text-brand-orange">~150 $</span>
                  <span className="text-base font-semibold text-brand-dark-blue">mesačne</span>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Priemerný plat v najmladšej krajine sveta, <strong>Južnom Sudáne</strong>. Kúpiť si pre seba alebo svoje dieťa predplatné AI jazykového modelu sa tak stáva prakticky nemožným.
                </p>
              </div>

              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Práve preto navštevujem miestne univerzity, zaujímam sa o lokálne podmienky a pripravujem <strong>medzinárodné vzdelávacie programy</strong>, ktoré dajú miestnym ľuďom šancu rozvíjať svoje zručnosti a zároveň im zabezpečia reálnu prax a prístup k moderným technológiám vrátane AI.
                </p>
              </div>
            </div>
          </div>

          {/* PROJEKT HIGHLIGHT */}
          <div className="mt-16 bg-brand-dark-blue text-white rounded-[2.5rem] p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '260px', backgroundRepeat: 'repeat' }} />
            <div className="relative z-10 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-brand-orange text-sm font-bold uppercase tracking-wide mb-6">
                <Users size={14} />
                Príklad spolupráce
              </div>
              <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-4 leading-tight">
                Remote Talent Hub &amp; University of Juba
              </h3>
              <p className="text-gray-200 text-lg leading-relaxed mb-4">
                Aktívne prepájame miestnych študentov s praxou na projektoch s globálnym významom prostredníctvom občianskeho združenia <strong>GenerationED</strong>.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Študenti získajú nielen skúsenosti a lepšie uplatnenie na európskom pracovnom trhu, ale aj prístup k moderným technológiám a nástrojom založeným na umelej inteligencii.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FINAL CTA */}
      <section className="section relative bg-brand-dark-blue text-white overflow-x-clip py-20 sm:py-32">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '300px', backgroundRepeat: 'repeat' }} />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-orange to-transparent" />

        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <Quote className="w-16 h-16 text-brand-orange/20 mx-auto mb-8" />
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-heading font-bold mb-8 text-white leading-tight break-words">
            Spojme svety, ktoré sa potrebujú
          </h2>
          <p className="text-base sm:text-xl text-gray-300 mb-12 leading-relaxed">
            Ak hľadáte partnera pre medzinárodnú spoluprácu, export alebo vzdelávacie projekty, rád sa s vami porozprávam.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/konzultace" className="inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 shadow-lg text-lg transform hover:scale-105">
              <Calendar size={24} className="mr-3" />
              Dohodnúť stretnutie
            </Link>
            <Link to="/o-mne" className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 border border-white/20 text-lg">
              Spoznať môj príbeh
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Afrika;
